import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { CareerJourney } from '../components/CareerJourney';
import { Hero } from '../components/Hero';
import { TimelineResultsSection } from '../components/TimelineResultsSection';
import { DayInLifeSection } from '../components/DayInLifeSection';
import { ArrowRightIcon, TrendingUpIcon, UsersIcon, CalendarIcon, HomeIcon, PhoneIcon, FileTextIcon, CheckCircleIcon, HeadphonesIcon, ShieldIcon, RocketIcon, TargetIcon, AwardIcon, MegaphoneIcon, ClipboardCheckIcon, HandshakeIcon, ShieldCheckIcon } from 'lucide-react';
export function CareerLanding() {
  const navigate = useNavigate();
  const {
    isAuthenticated
  } = useAuth();
  const handleOnboardingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/onboarding');
    } else {
      navigate('/auth/register', {
        state: {
          redirectTo: '/onboarding'
        }
      });
    }
  };
  return <div className="w-full min-h-screen bg-[#F1F1F1]" data-id="element-7878">
      {/* Hero Section - from Home */}
      <Hero data-id="element-7879" />

      {/* Timeline Results Section - from Home */}
      <TimelineResultsSection data-id="element-7880" />

      {/* Day in Life Section - from Home */}
      <DayInLifeSection data-id="element-7881" />

      {/* Why Real Estate Section */}
      <section className="py-20 bg-[#F1F1F1]" data-id="element-7882">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7883">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-7884">
            <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-4" data-id="element-7885">
              De ce Real Estate?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto" data-id="element-7886">
              Industria imobiliară oferă oportunități unice de creștere și
              dezvoltare profesională
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-7887">
            {[{
            icon: TrendingUpIcon,
            title: 'Venituri nelimitate',
            description: 'Câștigi în funcție de performanță, fără plafonare. Cu fiecare tranzacție, veniturile tale cresc.',
            color: 'from-[#357BEB] to-[#2868d8]'
          }, {
            icon: CalendarIcon,
            title: 'Flexibilitate totală',
            description: 'Îți gestionezi propriul program. Lucrezi când și cât vrei, adaptându-te stilului tău de viață.',
            color: 'from-[#21A366] to-[#1a8555]'
          }, {
            icon: AwardIcon,
            title: 'Dezvoltare continuă',
            description: 'Acces la training profesional, mentorat și resurse pentru a-ți dezvolta abilitățile constant.',
            color: 'from-[#FF6B35] to-[#e55a2b]'
          }].map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow" data-id="element-7888">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 shadow-lg`} data-id="element-7889">
                  <item.icon className="w-8 h-8 text-white" data-id="element-7890" />
                </div>
                <h3 className="text-xl font-bold text-[#132C53] mb-3" data-id="element-7891">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-7892">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Become Agent Section */}
      <section className="py-20 bg-white" data-id="element-7893">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7894">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-7895">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-7896">
              <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-6" data-id="element-7897">
                De ce să devii agent imobiliar?
              </h2>
              <div className="space-y-6" data-id="element-7898">
                {[{
                number: '01',
                title: 'Independență financiară',
                description: 'Construiește-ți propriul business și controlează-ți veniturile'
              }, {
                number: '02',
                title: 'Impact real',
                description: 'Ajuți oamenii să găsească casa visurilor lor sau să facă investiții inteligente'
              }, {
                number: '03',
                title: 'Networking valoros',
                description: 'Construiești relații cu profesioniști din diverse domenii'
              }, {
                number: '04',
                title: 'Satisfacție profesională',
                description: 'Fiecare tranzacție finalizată aduce împlinire și recunoaștere'
              }].map((item, index) => <motion.div key={item.number} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.1
              }} className="flex items-start space-x-4" data-id="element-7899">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#357BEB] to-[#21A366] flex items-center justify-center text-white font-bold shadow-lg" data-id="element-7900">
                      {item.number}
                    </div>
                    <div data-id="element-7901">
                      <h3 className="text-lg font-bold text-[#132C53] mb-1" data-id="element-7902">
                        {item.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-7903">{item.description}</p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative" data-id="element-7904">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#357BEB] to-[#21A366] p-1 shadow-2xl" data-id="element-7905">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-[#132C53] via-[#1e4278] to-[#357BEB] flex items-center justify-center relative overflow-hidden group" data-id="element-7906">
                  {/* Animated Background Glow */}
                  <motion.div className="absolute inset-0 opacity-30" animate={{
                  background: ['radial-gradient(circle at 50% 50%, rgba(53, 123, 235, 0.3) 0%, transparent 70%)', 'radial-gradient(circle at 50% 50%, rgba(33, 163, 102, 0.3) 0%, transparent 70%)', 'radial-gradient(circle at 50% 50%, rgba(53, 123, 235, 0.3) 0%, transparent 70%)']
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-7907" />

                  {/* Orbiting Icons */}
                  {[{
                  icon: RocketIcon,
                  angle: 0,
                  delay: 0,
                  color: '#357BEB'
                }, {
                  icon: HeadphonesIcon,
                  angle: 90,
                  delay: 0.5,
                  color: '#21A366'
                }, {
                  icon: UsersIcon,
                  angle: 180,
                  delay: 1,
                  color: '#357BEB'
                }, {
                  icon: TargetIcon,
                  angle: 270,
                  delay: 1.5,
                  color: '#21A366'
                }].map((item, index) => {
                  const Icon = item.icon;
                  return <motion.div key={index} className="absolute" style={{
                    left: '50%',
                    top: '50%'
                  }} animate={{
                    rotate: [item.angle, item.angle + 360],
                    x: [Math.cos(item.angle * Math.PI / 180) * 80, Math.cos((item.angle + 360) * Math.PI / 180) * 80],
                    y: [Math.sin(item.angle * Math.PI / 180) * 80, Math.sin((item.angle + 360) * Math.PI / 180) * 80],
                    scale: [1, 1.2, 1]
                  }} transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: 'linear',
                    delay: item.delay,
                    scale: {
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }
                  }} data-id="element-7908">
                        <motion.div className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)'
                    }} whileHover={{
                      scale: 1.3
                    }} data-id="element-7909">
                          <Icon className="w-5 h-5 text-white" data-id="element-7910" />
                        </motion.div>
                      </motion.div>;
                })}

                  {/* Central Badge */}
                  <div className="relative z-10 text-center p-8" data-id="element-7911">
                    <motion.div className="relative inline-block" whileHover={{
                    scale: 1.1,
                    rotateY: 15,
                    rotateX: -5
                  }} transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 20
                  }} style={{
                    perspective: '1000px',
                    transformStyle: 'preserve-3d'
                  }} data-id="element-7912">
                      {/* Glowing Effect */}
                      <motion.div className="absolute inset-0 rounded-full blur-2xl" animate={{
                      boxShadow: ['0 0 40px 10px rgba(53, 123, 235, 0.6)', '0 0 60px 20px rgba(33, 163, 102, 0.6)', '0 0 40px 10px rgba(53, 123, 235, 0.6)']
                    }} transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} data-id="element-7913" />

                      {/* Badge Container */}
                      <motion.div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 flex items-center justify-center shadow-2xl" animate={{
                      boxShadow: ['0 10px 40px rgba(234, 179, 8, 0.5)', '0 15px 50px rgba(234, 179, 8, 0.7)', '0 10px 40px rgba(234, 179, 8, 0.5)']
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} data-id="element-7914">
                        {/* Inner Circle */}
                        <div className="absolute inset-2 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center" data-id="element-7915">
                          {/* Trophy Icon */}
                          <motion.div animate={{
                          rotate: [0, 5, -5, 0],
                          scale: [1, 1.05, 1]
                        }} transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }} data-id="element-7916">
                            <AwardIcon className="w-16 h-16 text-yellow-900" strokeWidth={2.5} data-id="element-7917" />
                          </motion.div>
                        </div>

                        {/* Shine Effect */}
                        <motion.div className="absolute inset-0 rounded-full" style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)'
                      }} animate={{
                        opacity: [0.3, 0.6, 0.3]
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }} data-id="element-7918" />
                      </motion.div>
                    </motion.div>

                    {/* Text Content */}
                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.3
                  }} className="mt-6" data-id="element-7919">
                      <h3 className="text-2xl font-bold text-white mb-2" data-id="element-7920">
                        Succes Garantat
                      </h3>
                      <p className="text-gray-200 text-sm" data-id="element-7921">
                        Cu sistemul nostru complet de suport și training
                      </p>
                    </motion.div>

                    {/* Sparkle Effects */}
                    {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 bg-white rounded-full" style={{
                    left: `${20 + Math.random() * 60}%`,
                    top: `${20 + Math.random() * 60}%`
                  }} animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: 'easeInOut'
                  }} data-id="element-7922" />)}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why CautLocuinta Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-7923">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7924">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-7925">
            <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-4" data-id="element-7926">
              De ce Cautlocuință.ro?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto" data-id="element-7927">
              Oferim cel mai complet ecosistem de suport pentru agenții
              imobiliari
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-7928">
            {[{
            icon: RocketIcon,
            title: 'Training complet',
            description: 'Masterclass profesionale și mentorat continuu pentru dezvoltare'
          }, {
            icon: UsersIcon,
            title: 'Leaduri garantate',
            description: 'Acces la baza noastră de clienți și proprietari verificați'
          }, {
            icon: ShieldIcon,
            title: 'Suport juridic',
            description: 'Echipă juridică dedicată pentru toate tranzacțiile tale'
          }, {
            icon: TargetIcon,
            title: 'Marketing profesional',
            description: 'Campanii de promovare și branding pentru proprietățile tale'
          }, {
            icon: HeadphonesIcon,
            title: 'Suport 24/7',
            description: 'Asistență tehnică și operațională oricând ai nevoie'
          }, {
            icon: TrendingUpIcon,
            title: 'Comisioane competitive',
            description: 'Sistem transparent de comisionare cu bonusuri de performanță'
          }].map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border-2 border-transparent hover:border-[#357BEB]" data-id="element-7929">
                <div className="w-12 h-12 rounded-xl bg-[#357BEB] bg-opacity-10 flex items-center justify-center mb-4" data-id="element-7930">
                  <item.icon className="w-6 h-6 text-[#357BEB]" data-id="element-7931" />
                </div>
                <h3 className="text-lg font-bold text-[#132C53] mb-2" data-id="element-7932">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-7933">{item.description}</p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-2xl p-8 text-white text-center" data-id="element-7934">
            <h3 className="text-2xl font-bold mb-3" data-id="element-7935">
              🎯 Totul pentru succesul tău
            </h3>
            <p className="text-lg text-gray-100 max-w-3xl mx-auto" data-id="element-7936">
              Nu ești singur în această călătorie. Avem toate resursele,
              instrumentele și expertiza necesare pentru a te transforma într-un
              agent de top.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Collaboration Steps Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-7937">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7938">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-7939">
            <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-4" data-id="element-7940">
              Pașii colaborării
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto" data-id="element-7941">
              Procesul simplu și transparent de a deveni agent Cautlocuință.ro
            </p>
          </motion.div>

          <div className="relative" data-id="element-7942">
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#357BEB] to-[#21A366] transform -translate-x-1/2 hidden md:block" data-id="element-7943"></div>

            <div className="space-y-12" data-id="element-7944">
              {[{
              icon: FileTextIcon,
              title: 'Aplicare online',
              description: 'Completezi formularul de aplicare cu datele tale de contact'
            }, {
              icon: PhoneIcon,
              title: 'Interviu telefonic',
              description: 'Discutăm despre experiența ta și așteptările tale'
            }, {
              icon: UsersIcon,
              title: 'Întâlnire la birou',
              description: 'Cunoști echipa și afli detalii despre colaborare'
            }, {
              icon: RocketIcon,
              title: 'On-boarding complet',
              description: 'Primești acces la platformă, training și primele leaduri'
            }, {
              icon: TargetIcon,
              title: 'Primele tranzacții',
              description: 'Începi să lucrezi cu suport complet din partea echipei'
            }, {
              icon: TrendingUpIcon,
              title: 'Creștere continuă',
              description: 'Dezvoltare constantă cu training și oportunități noi'
            }].map((step, index) => <motion.div key={step.title} initial={{
              opacity: 0,
              x: index % 2 === 0 ? -30 : 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-7945">
                  <div className="flex-1 bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100" data-id="element-7946">
                    <div className="flex items-start space-x-4" data-id="element-7947">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#357BEB] to-[#21A366] flex items-center justify-center text-white font-bold shadow-lg" data-id="element-7948">
                        {index + 1}
                      </div>
                      <div className="flex-1" data-id="element-7949">
                        <h3 className="text-xl font-bold text-[#132C53] mb-2" data-id="element-7950">
                          {step.title}
                        </h3>
                        <p className="text-gray-600" data-id="element-7951">{step.description}</p>
                      </div>
                      <step.icon className="w-8 h-8 text-[#357BEB]" data-id="element-7952" />
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Concrete Offerings Section */}
      <section className="py-20 bg-white" data-id="element-7953">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7954">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-7955">
            <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-4" data-id="element-7956">
              Ce oferim concret
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto" data-id="element-7957">
              Beneficii tangibile pentru fiecare agent din echipa noastră
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7958">
            {[{
            icon: HomeIcon,
            title: 'Platformă CRM',
            description: 'Sistem complet de management clienți și proprietăți'
          }, {
            icon: PhoneIcon,
            title: 'Liste proprietari',
            description: 'Acces la baze de date verificate și actualizate'
          }, {
            icon: UsersIcon,
            title: 'Leaduri clienți',
            description: 'Clienți calificați alocați automat'
          }, {
            icon: MegaphoneIcon,
            title: 'Marketing digital',
            description: 'Campanii profesionale pentru proprietățile tale'
          }, {
            icon: FileTextIcon,
            title: 'Template-uri',
            description: 'Contracte și documente legale pregătite'
          }, {
            icon: ShieldIcon,
            title: 'Asistență juridică',
            description: 'Consultanță legală pentru toate tranzacțiile'
          }, {
            icon: CalendarIcon,
            title: 'Training săptămânal',
            description: 'Sesiuni live de dezvoltare profesională'
          }, {
            icon: HeadphonesIcon,
            title: 'Suport dedicat',
            description: 'Echipă de suport disponibilă permanent'
          }].map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow text-center border-2 border-transparent hover:border-[#357BEB]" data-id="element-7959">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#357BEB] to-[#21A366] flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-7960">
                  <item.icon className="w-7 h-7 text-white" data-id="element-7961" />
                </div>
                <h3 className="text-lg font-bold text-[#132C53] mb-2" data-id="element-7962">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-7963">{item.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CLC Recipe Section - REPLACED WITH NEW COMPREHENSIVE SECTION */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-7964">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7965">
          {/* Intro Card - BOLD VISUAL HIERARCHY */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative rounded-3xl overflow-hidden shadow-2xl mb-16" data-id="element-7966">
            {/* Subtle Pattern Overlay */}
            <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-7967">
              <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle, #357BEB 1px, transparent 1px)`,
              backgroundSize: '20px 20px'
            }} data-id="element-7968"></div>
            </div>

            {/* Two-tone Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-white via-white to-blue-50" data-id="element-7969"></div>

            {/* Floating Pulsing Badge */}
            <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} animate={{
            scale: [1, 1.05, 1],
            boxShadow: ['0 0 0 0 rgba(53, 123, 235, 0.4)', '0 0 0 10px rgba(53, 123, 235, 0)', '0 0 0 0 rgba(53, 123, 235, 0)']
          }} transition={{
            scale: {
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            },
            boxShadow: {
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }
          }} className="absolute top-8 right-8 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg z-10" data-id="element-7970">
              Metodologie CLC – Unică în România
            </motion.div>

            <div className="relative max-w-4xl mx-auto text-center px-8 py-16 md:py-20" data-id="element-7971">
              {/* Large Icon/Emoji */}
              <motion.div initial={{
              scale: 0,
              rotate: -180
            }} whileInView={{
              scale: 1,
              rotate: 0
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              delay: 0.1
            }} className="text-8xl mb-8" data-id="element-7972">
                🎯
              </motion.div>

              {/* Oversized Heading with Gradient Text */}
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight" data-id="element-7973">
                <span className="bg-gradient-to-r from-[#357BEB] via-[#2868d8] to-[#21A366] bg-clip-text text-transparent" data-id="element-7974">
                  Rețeta CLC
                </span>
                <span className="block text-[#132C53] mt-2" data-id="element-7975">
                  Metodologia tranzacției Step by Step
                </span>
              </motion.h2>

              {/* Description */}
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="text-xl md:text-2xl text-gray-700 mb-12 leading-relaxed max-w-3xl mx-auto" data-id="element-7976">
                La Cautlocuință.ro, tranzacțiile nu se fac la întâmplare. Avem o
                rețetă clară, structurată în acțiuni concrete, în care agenția
                are know-how și experiență pentru fiecare pas. Această
                metodologie este pusă la dispoziția fiecărui colaborator CLC.
              </motion.p>

              {/* Larger Bullet Points with Animated Checkmarks */}
              <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-lg border border-gray-200" data-id="element-7977">
                <ul className="space-y-6 text-left max-w-2xl mx-auto" data-id="element-7978">
                  {[{
                  text: 'Tranzacția este împărțită în',
                  highlight: 'pași clari',
                  suffix: ', ușor de urmărit'
                }, {
                  text: 'Pentru fiecare pas există',
                  highlight: 'proceduri, scripturi și modele',
                  suffix: ''
                }, {
                  text: 'Agentul',
                  highlight: 'nu este lăsat singur',
                  suffix: ' să improvizeze'
                }].map((item, index) => <motion.li key={index} initial={{
                  opacity: 0,
                  x: -30
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.5 + index * 0.1
                }} className="flex items-start space-x-4" data-id="element-7979">
                      <motion.div initial={{
                    scale: 0,
                    rotate: -180
                  }} whileInView={{
                    scale: 1,
                    rotate: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 12,
                    delay: 0.6 + index * 0.1
                  }} className="flex-shrink-0" data-id="element-7980">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#21A366] to-[#1a8555] flex items-center justify-center shadow-lg" data-id="element-7981">
                          <CheckCircleIcon className="w-5 h-5 text-white" data-id="element-7982" />
                        </div>
                      </motion.div>
                      <span className="text-lg md:text-xl text-gray-700 leading-relaxed pt-0.5" data-id="element-7983">
                        {item.text}{' '}
                        <strong className="text-[#132C53]" data-id="element-7984">
                          {item.highlight}
                        </strong>
                        {item.suffix}
                      </span>
                    </motion.li>)}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Timeline Section */}
          <div className="mb-16" data-id="element-7985">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-12" data-id="element-7986">
              <h3 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-4" data-id="element-7987">
                Tranzacția Step by Step – cum lucrăm concret
              </h3>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-7988">
              {[{
              number: '1',
              icon: PhoneIcon,
              title: 'Prospectare & Apeluri',
              description: 'Identificăm proprietarii potriviți și îi contactăm după scripturi testate. Agentul este asistat în apelurile de prospectare – poate primi feedback live și exemple concrete.',
              color: 'from-[#357BEB] to-[#2868d8]'
            }, {
              number: '2',
              icon: ClipboardCheckIcon,
              title: 'Calificare & Analiză',
              description: 'Analizăm proprietatea, cererea și contextul. Agentul este ghidat ce întrebări să pună și ce informații să adune.',
              color: 'from-[#2868d8] to-[#1e5bb8]'
            }, {
              number: '3',
              icon: FileTextIcon,
              title: 'Contract & Setare Colaborare',
              description: 'Configurăm corect colaborarea cu proprietarul. Oferim modele de contract, explicații și verificări juridice.',
              color: 'from-[#1e5bb8] to-[#132C53]'
            }, {
              number: '4',
              icon: MegaphoneIcon,
              title: 'Promovare & Portofoliu',
              description: 'Activezi promovarea în ecosistemul CLC. Agenția are proceduri și know-how pentru anunțuri, texte și vizibilitate maximă.',
              color: 'from-[#132C53] to-[#21A366]'
            }, {
              number: '5',
              icon: UsersIcon,
              title: 'Matching & Leaduri',
              description: 'Potrivim proprietatea cu cererile existente. Sistemul generează matching intern + extern, iar agentul este asistat în contactarea clienților.',
              color: 'from-[#21A366] to-[#1a8555]'
            }, {
              number: '6',
              icon: HomeIcon,
              title: 'Vizionări Organizate',
              description: 'Planificăm și structurăm vizionările. Agentul primește suport pentru: confirmări, follow-up, modul de prezentare și gestionarea obiecțiilor.',
              color: 'from-[#1a8555] to-[#357BEB]'
            }, {
              number: '7',
              icon: HandshakeIcon,
              title: 'Ofertare & Negociere',
              description: 'Agentul este asistat individual în discuțiile sensibile: ofertă, contraofertă, negociere de preț și condiții.',
              color: 'from-[#357BEB] to-[#2868d8]'
            }, {
              number: '8',
              icon: ShieldCheckIcon,
              title: 'Verificări & Legal',
              description: 'Departamentul juridic și echipa de suport verifică actele, clauzele și siguranța tranzacției. Agentul nu trebuie să fie jurist, are echipă în spate.',
              color: 'from-[#2868d8] to-[#132C53]'
            }, {
              number: '9',
              icon: CheckCircleIcon,
              title: 'Semnare, Facturare & Arhivare',
              description: 'Închidem tranzacția complet: contracte semnate, facturi emise, comision încasat și tranzacția arhivată corect în sistem.',
              color: 'from-[#132C53] to-[#21A366]'
            }].map((step, index) => <motion.div key={step.number} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-[#357BEB] cursor-pointer group" data-id="element-7989">
                  <div className="flex items-start space-x-4 mb-4" data-id="element-7990">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform`} data-id="element-7991">
                      <step.icon className="w-6 h-6 text-white" data-id="element-7992" />
                    </div>
                    <div className="flex-1" data-id="element-7993">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-7994">
                        <span className="text-xs font-bold text-gray-400" data-id="element-7995">
                          PASUL {step.number}
                        </span>
                      </div>
                      <h4 className="text-lg font-bold text-[#132C53] mb-2" data-id="element-7996">
                        {step.title}
                      </h4>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-7997">
                    {step.description}
                  </p>
                </motion.div>)}
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="bg-[#357BEB] bg-opacity-10 rounded-2xl p-6 text-center" data-id="element-7998">
              <p className="text-lg text-gray-700 font-medium" data-id="element-7999">
                💡 Pentru fiecare pas există proceduri clare, documente,
                scripturi și suport în timp real. Agentul nu învață din greșeli,
                ci din metodologie.
              </p>
            </motion.div>
          </div>

          {/* Support Section - Two Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12" data-id="element-8000">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-8001">
              <h3 className="text-3xl font-bold text-[#132C53] mb-6" data-id="element-8002">
                Suport Individual 7/7, pentru fiecare agent colaborator
              </h3>
              <div className="space-y-4" data-id="element-8003">
                {['Fiecare agent are propriul canal de suport individual, nu doar un grup general.', 'Toate departamentele sunt la dispoziția ta: imobiliar, juridic, fiscal, tehnic, AI – direct, în timp real.', 'Te asistăm în apeluri de prospectare, în organizarea și finalizarea vizionărilor, în închiderea tranzacțiilor și în situațiile delicate.', 'Nu oferim doar răspunsuri, ci soluții concrete, adaptate fiecărui agent și fiecărei tranzacții.'].map((text, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.1
              }} className="flex items-start space-x-3" data-id="element-8004">
                    <CheckCircleIcon className="w-6 h-6 text-[#21A366] flex-shrink-0 mt-0.5" data-id="element-8005" />
                    <p className="text-gray-700 leading-relaxed" data-id="element-8006">{text}</p>
                  </motion.div>)}
              </div>
              <div className="mt-8 bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-2xl p-6 text-white" data-id="element-8007">
                <p className="text-lg font-semibold" data-id="element-8008">
                  Metodologia noastră este unică: tranzacții structurate +
                  suport individual = agenți care performează mai repede.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-white rounded-2xl p-8 shadow-xl border-2 border-gray-100" data-id="element-8009">
              <div className="text-center mb-6" data-id="element-8010">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#357BEB] to-[#21A366] rounded-2xl flex items-center justify-center mb-4 shadow-lg" data-id="element-8011">
                  <HeadphonesIcon className="w-10 h-10 text-white" data-id="element-8012" />
                </div>
                <h4 className="text-2xl font-bold text-[#132C53]" data-id="element-8013">
                  Asistență Completă
                </h4>
              </div>
              <div className="space-y-4" data-id="element-8014">
                {[{
                icon: PhoneIcon,
                text: 'Prospectare asistată'
              }, {
                icon: HomeIcon,
                text: 'Vizionări asistate'
              }, {
                icon: HandshakeIcon,
                text: 'Negociere asistată'
              }, {
                icon: CheckCircleIcon,
                text: 'Închidere tranzacție asistată'
              }].map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: index * 0.1
              }} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl" data-id="element-8015">
                    <div className="w-10 h-10 bg-[#357BEB] bg-opacity-10 rounded-lg flex items-center justify-center" data-id="element-8016">
                      <item.icon className="w-5 h-5 text-[#357BEB]" data-id="element-8017" />
                    </div>
                    <span className="font-semibold text-gray-700" data-id="element-8018">
                      {item.text}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>

          {/* Final Callout */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-3xl p-8 md:p-12 text-white text-center shadow-2xl" data-id="element-8019">
            <div className="max-w-4xl mx-auto" data-id="element-8020">
              <div className="text-5xl mb-6" data-id="element-8021">🎯</div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-8022">
                Rețeta CLC nu este doar un set de reguli
              </h3>
              <p className="text-xl text-gray-100 leading-relaxed" data-id="element-8023">
                Este un sistem complet de lucru. Tu vii cu ambiția, noi îți dăm
                metodologia și suportul pentru fiecare pas al tranzacției.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 24/7 Support Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-8024">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-8025">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-gradient-to-br from-[#132C53] via-[#1e4278] to-[#357BEB] rounded-3xl p-12 text-white text-center shadow-2xl" data-id="element-8026">
            <div className="max-w-3xl mx-auto" data-id="element-8027">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="inline-block bg-white bg-opacity-20 backdrop-blur-sm px-6 py-3 rounded-full mb-6" data-id="element-8028">
                <span className="text-lg font-semibold" data-id="element-8029">
                  🎧 Disponibili permanent
                </span>
              </motion.div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-8030">
                Suport 24/7 pentru tine
              </h2>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed" data-id="element-8031">
                Indiferent de oră sau zi, echipa noastră este aici pentru tine.
                Asistență tehnică, consultanță juridică, suport operațional —
                totul la un telefon distanță.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" data-id="element-8032">
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6" data-id="element-8033">
                  <HeadphonesIcon className="w-12 h-12 mx-auto mb-4" data-id="element-8034" />
                  <h3 className="text-lg font-bold mb-2" data-id="element-8035">Suport tehnic</h3>
                  <p className="text-gray-200 text-sm" data-id="element-8036">
                    Asistență platformă și instrumente
                  </p>
                </div>
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6" data-id="element-8037">
                  <ShieldIcon className="w-12 h-12 mx-auto mb-4" data-id="element-8038" />
                  <h3 className="text-lg font-bold mb-2" data-id="element-8039">
                    Consultanță juridică
                  </h3>
                  <p className="text-gray-200 text-sm" data-id="element-8040">
                    Răspunsuri la întrebări legale
                  </p>
                </div>
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6" data-id="element-8041">
                  <UsersIcon className="w-12 h-12 mx-auto mb-4" data-id="element-8042" />
                  <h3 className="text-lg font-bold mb-2" data-id="element-8043">Mentorat activ</h3>
                  <p className="text-gray-200 text-sm" data-id="element-8044">
                    Ghidare de la agenți seniori
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-white" data-id="element-8045">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-8046">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-8047">
            <div className="max-w-3xl mx-auto" data-id="element-8048">
              <div className="text-6xl mb-6" data-id="element-8049">🚀</div>
              <h2 className="text-3xl md:text-5xl font-bold text-[#132C53] mb-6" data-id="element-8050">
                Gata să începi?
              </h2>
              <p className="text-xl text-gray-600 mb-12 leading-relaxed" data-id="element-8051">
                Alătură-te echipei Cautlocuință.ro și construiește cariera pe
                care ți-o dorești. Totul începe cu un singur pas.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-8052">
                <button onClick={handleOnboardingClick} className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white px-10 py-5 rounded-xl font-bold text-xl hover:shadow-2xl transition-all transform hover:scale-105" data-id="element-8053">
                  <span data-id="element-8054">Începe On-boarding-ul Acum</span>
                  <ArrowRightIcon className="w-6 h-6" data-id="element-8055" />
                </button>
                <Link to="/cursuri" className="inline-flex items-center justify-center space-x-2 bg-white text-[#132C53] px-10 py-5 rounded-xl font-bold text-xl border-2 border-[#132C53] hover:shadow-xl transition-all" data-id="element-8056">
                  <span data-id="element-8057">Explorează Cursurile</span>
                </Link>
              </div>
              <p className="text-gray-500 mt-8 text-sm" data-id="element-8058">
                💡 Procesul de on-boarding durează aproximativ 2-3 minute
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}